import React, { useCallback, useEffect, useRef, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Upload, FileText, Trash2, Download, Image as ImageIcon, FileArchive, FileSpreadsheet, FileCode } from "lucide-react";
import { toast } from "sonner";

function iconFor(ct = "", name = "") {
  const n = (name || "").toLowerCase();
  if (ct.startsWith("image/") || /\.(png|jpe?g|gif|webp|svg)$/i.test(n)) return ImageIcon;
  if (ct === "application/pdf" || n.endsWith(".pdf")) return FileText;
  if (ct.includes("zip") || n.endsWith(".zip")) return FileArchive;
  if (ct.includes("spreadsheet") || /\.(csv|xlsx?|)$/i.test(n)) return FileSpreadsheet;
  if (/\.(json|xml|js|ts|py|css|html)$/i.test(n)) return FileCode;
  return FileText;
}

function fmtBytes(n) {
  if (!n) return "—";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0, val = Number(n);
  while (val >= 1024 && i < units.length - 1) { val /= 1024; i++; }
  return `${val.toFixed(val >= 10 ? 0 : 1)} ${units[i]}`;
}

export default function FileManager() {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [description, setDescription] = useState("");
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("");
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [f, p] = await Promise.all([
        api.get("/files"),
        api.get("/projects").catch(() => ({ data: [] })),
      ]);
      setFiles(f.data || []);
      setProjects(p.data || []);
    } catch (error) {
      console.error("Failed to load files:", error);
      toast.error("Failed to load files");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      toast.error("File exceeds 25 MB limit");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (description) fd.append("description", description);
      if (projectId) fd.append("project_id", projectId);
      await api.post("/files", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Uploaded ${file.name}`);
      setDescription("");
      if (inputRef.current) inputRef.current.value = "";
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const download = async (f) => {
    try {
      const res = await api.get(`/files/${f.file_id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = f.original_filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Download failed");
    }
  };

  const remove = async (f) => {
    if (!window.confirm(`Delete "${f.original_filename}"?`)) return;
    try {
      await api.delete(`/files/${f.file_id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0];
    if (file) upload(file);
  };

  return (
    <DashboardLayout
      title="File Manager"
      subtitle="Secure client files, project deliverables and internal documents"
    >
      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleDrop}
        data-testid="file-dropzone"
        className="rounded-xl border-2 border-dashed border-zinc-800 bg-zinc-900/30 p-8 mb-6 text-center hover:border-blue-500/50 transition-colors"
      >
        <Upload className="w-8 h-8 mx-auto text-zinc-500 mb-3" />
        <div className="text-sm text-zinc-300 mb-1">Drag & drop a file here, or click to browse</div>
        <div className="text-xs text-zinc-500 mb-4">Up to 25 MB · encrypted at rest · tenant-isolated</div>
        <div className="max-w-xl mx-auto grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
          <input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Description (optional)"
            data-testid="file-description"
            className="h-9 bg-zinc-950 border border-zinc-800 rounded-md px-3 text-xs text-zinc-200"
          />
          <select
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
            data-testid="file-project"
            className="h-9 bg-zinc-950 border border-zinc-800 rounded-md px-2 text-xs text-zinc-200"
          >
            <option value="">Attach to project (optional)</option>
            {projects.map((p) => <option key={p.project_id} value={p.project_id}>{p.name}</option>)}
          </select>
        </div>
        <input
          ref={inputRef}
          type="file"
          className="hidden"
          data-testid="file-input"
          onChange={(e) => upload(e.target.files?.[0])}
        />
        <button
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          data-testid="file-upload-btn"
          className="btn-scale h-9 px-4 rounded-md bg-blue-500 hover:bg-blue-400 text-white text-sm font-medium disabled:opacity-50"
        >
          {uploading ? "Uploading…" : "Choose file"}
        </button>
      </div>

      <div className="rounded-xl border border-zinc-800 overflow-x-auto" data-testid="file-list">
        <table className="w-full text-sm min-w-[720px]">
          <thead>
            <tr className="text-left text-xs text-zinc-500 border-b border-zinc-800 bg-zinc-900/40">
              <th className="p-3">File</th>
              <th className="p-3">Size</th>
              <th className="p-3">Project</th>
              <th className="p-3">Uploaded by</th>
              <th className="p-3">When</th>
              <th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {files.map((f) => {
              const Icon = iconFor(f.content_type, f.original_filename);
              const projName = projects.find((p) => p.project_id === f.project_id)?.name;
              return (
                <tr key={f.file_id} className="border-b border-zinc-800 hover:bg-zinc-900/40" data-testid={`file-row-${f.file_id}`}>
                  <td className="p-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <Icon className="w-4 h-4 text-blue-400 shrink-0" />
                      <div className="min-w-0">
                        <div className="text-zinc-100 truncate">{f.original_filename}</div>
                        {f.description && <div className="text-xs text-zinc-500 truncate">{f.description}</div>}
                      </div>
                    </div>
                  </td>
                  <td className="p-3 text-zinc-400 text-xs">{fmtBytes(f.size)}</td>
                  <td className="p-3 text-zinc-400 text-xs">{projName || "—"}</td>
                  <td className="p-3 text-zinc-400 text-xs truncate max-w-[180px]">{f.uploaded_by_email || "—"}</td>
                  <td className="p-3 text-zinc-500 text-xs">{(f.created_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="p-3 text-right">
                    <button data-testid={`file-download-${f.file_id}`} onClick={() => download(f)} className="btn-scale h-8 w-8 grid place-items-center rounded-md text-zinc-400 hover:text-blue-300 hover:bg-zinc-800 inline-flex"><Download className="w-3.5 h-3.5" /></button>
                    <button data-testid={`file-delete-${f.file_id}`} onClick={() => remove(f)} className="btn-scale h-8 w-8 grid place-items-center rounded-md text-zinc-400 hover:text-rose-400 hover:bg-zinc-800 inline-flex ml-1"><Trash2 className="w-3.5 h-3.5" /></button>
                  </td>
                </tr>
              );
            })}
            {files.length === 0 && (
              <tr><td colSpan={6} className="p-8 text-center text-zinc-500">No files yet. Drop or choose one above.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </DashboardLayout>
  );
}
